import { QueryClient } from '@tanstack/react-query';
import { mutationRetry, retryDelay, shouldRetryQuery } from './retry';

/**
 * Stale times, in one place so a call site can say *what kind of data* it is
 * rather than guessing a number (docs/plan/06-frontend-v1.md §1).
 */
export const STALE_TIMES = {
  /** Message lists, event ranges, contact pages — cheap to refetch, changes often. */
  list: 30_000,
  /** Folder trees, calendar/address-book metadata, account settings. */
  metadata: 5 * 60_000,
  /**
   * Message bodies. A delivered message body is immutable; its *flags* change,
   * and those are patched into the cache rather than refetched.
   */
  immutable: Number.POSITIVE_INFINITY,
} as const;

/**
 * How long one message detail stays fresh.
 *
 * The body is immutable. Whether its remote images were loaded is not: the
 * server decided that from the "always load" rules of the moment it answered
 * (ADR 0018), and a rule saved or removed since -- here, or on another device
 * -- changes the answer. A detail restored from the offline cache was decided
 * up to days ago. So a body the SERVER chose the mode of, and that has remote
 * images at all, is always stale: it is shown from the cache at once and asked
 * for again each time it is opened. Not after a few minutes: the offline cache
 * restores a copy with its age intact, and "Always load", reload, reopen
 * within those minutes showed "blocked" and offered the same rule again.
 * A body the reader chose the mode of (`remote`) is exactly what they asked
 * for, and one with no remote images has nothing a rule could change.
 */
export function messageDetailStaleTime(
  remote: 'block' | 'load' | undefined,
): (query: { state: { data?: unknown } }) => number {
  return (query) => {
    if (remote !== undefined) return STALE_TIMES.immutable;
    const info = (query.state.data as { remoteContent?: { blocked: number; loaded: number } } | undefined)
      ?.remoteContent;
    return info !== undefined && info.blocked + info.loaded > 0 ? 0 : STALE_TIMES.immutable;
  };
}

const GC_TIME = 30 * 60_000;

/**
 * The app's QueryClient.
 *
 * Two defaults are deliberate and load-bearing for a mail client:
 *
 * - **`refetchOnWindowFocus: false`.** Focus refetching is the single most
 *   common way a mail list jumps under a user who is mid-triage: alt-tab back,
 *   the list re-sorts, and the keyboard cursor is now on a different message.
 *   The WebSocket (WP-17) is the freshness mechanism; focus is not.
 * - **`refetchOnReconnect: true`.** Coming back from offline is exactly the
 *   moment cached mail *is* stale and the user expects a sync.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: STALE_TIMES.list,
        gcTime: GC_TIME,
        retry: shouldRetryQuery,
        retryDelay,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
      },
      mutations: {
        retry: mutationRetry,
        retryDelay,
      },
    },
  });
}

/**
 * Deterministic client for tests: no retries, no GC racing the assertions.
 * Pair it with `createMockApi({ latencyMs: 0 })`.
 */
export function createTestQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: { retry: false, gcTime: Number.POSITIVE_INFINITY, staleTime: 0 },
      mutations: { retry: false },
    },
  });
}
